import { useEffect, useRef } from 'react'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import styled from 'styled-components'

import { ANONYMOUS, loadPaymentWidget } from '@tosspayments/payment-widget-sdk'

import { usePostResultPayment } from '~/features/payment/queries/usePayment'
import Close from '~/shared/assets/icons/Close'
import { isPaymentModalOpenState } from '~/shared/states'
import theme from '~/shared/styles/theme'

export default function PaymentModal() {
  const navigate = useNavigate()
  const { mutate: postResultPaymentMutate } = usePostResultPayment()

  const { enqueueSnackbar } = useSnackbar()
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useRecoilState(
    isPaymentModalOpenState,
  )
  const paymentWidgetRef = useRef(null)
  const paymentMethodsWidgetRef = useRef(null)
  const agreementWidgetRef = useRef(null)

  useEffect(() => {
    ;(async () => {
      const paymentWidget = await loadPaymentWidget(
        import.meta.env.VITE_TOSSPAYMENTS_CLIENT_KEY,
        ANONYMOUS,
      ) // 비회원 customerKey

      if (paymentWidgetRef.current == null) {
        paymentWidgetRef.current = paymentWidget as any
      }

      /**
       * 결제창을 렌더링합니다.
       * @docs https://docs.tosspayments.com/reference/widget-sdk#renderpaymentmethods%EC%84%A0%ED%83%9D%EC%9E%90-%EA%B2%B0%EC%A0%9C-%EA%B8%88%EC%95%A1
       */

      const paymentMethodsWidget = (
        paymentWidgetRef.current as any
      )?.renderPaymentMethods(
        '#payment-method',
        { value: isPaymentModalOpen.contents.amount },
        { variantKey: 'DEFAULT' },
      )

      /**
       * 약관을 렌더링합니다.
       * @docs https://docs.tosspayments.com/reference/widget-sdk#renderagreement%EC%84%A0%ED%83%9D%EC%9E%90-%EC%98%B5%EC%85%98
       */
      agreementWidgetRef.current = (
        paymentWidgetRef.current as any
      )?.renderAgreement('#agreement', { variantKey: 'DEFAULT' })

      paymentMethodsWidgetRef.current = paymentMethodsWidget
    })()
  }, [isPaymentModalOpen.contents.amount, isPaymentModalOpen.contents.orderId])

  return (
    <Background>
      <Wrapper>
        <CloseIcon
          onClick={() => {
            postResultPaymentMutate({
              paymentInfo: {
                success: false,
                order_code: isPaymentModalOpen.contents.orderId || '',
                error_code: 'CANCLE',
              },
            })
            setIsPaymentModalOpen({
              contents: {
                productName: '',
                amount: 0,
                orderId: '',
                userId: 0,
                paymentKey: '',
                paymentType: '',
              },
              isOpen: false,
            })
          }}
        />
        <div id="payment-method" />
        <div id="agreement" />
        <ButtonContainer>
          <PaymentButton
            onClick={async () => {
              const paymentWidget = paymentWidgetRef.current

              try {
                /**
                 * 결제 요청
                 * @docs https://docs.tosspayments.com/reference/widget-sdk#requestpayment%EA%B2%B0%EC%A0%9C-%EC%A0%95%EB%B3%B4
                 */
                await (paymentWidget as any)?.requestPayment({
                  orderId: isPaymentModalOpen.contents.orderId,
                  orderName: isPaymentModalOpen.contents.productName,
                  amount: isPaymentModalOpen.contents.amount,
                  customerName: localStorage.getItem('starlike-user-name'),
                  successUrl:
                    window.location.origin +
                    '/success' +
                    window.location.search,
                  failUrl:
                    window.location.origin + '/fail' + window.location.search,
                })
              } catch (error: any) {
                enqueueSnackbar('결제에 실패했습니다.', { variant: 'error' })
                postResultPaymentMutate({
                  paymentInfo: {
                    success: false,
                    order_code: isPaymentModalOpen.contents.orderId || '',
                    payment_key: isPaymentModalOpen.contents.paymentKey || '',
                    amount: Number(isPaymentModalOpen.contents.amount),
                    payment_type:
                      isPaymentModalOpen.contents.paymentType || undefined,
                    error_code: error.code,
                  },
                })
                setIsPaymentModalOpen({
                  ...isPaymentModalOpen,
                  isOpen: false,
                })

                return navigate('/fail')
              }
            }}
          >
            결제하기
          </PaymentButton>
        </ButtonContainer>
      </Wrapper>
    </Background>
  )
}

const Background = styled.div`
  position: fixed;
  z-index: ${theme.zIndex.modal};
  inset: 0;
  background: rgb(0 0 0 / 80%);
`

const Wrapper = styled.div`
  position: fixed;
  z-index: ${theme.zIndex.modal};
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  width: 30%;
  max-width: 540px;
  height: auto;
  margin: 0 auto;

  background-color: ${theme.colors.white};
  backdrop-filter: blur(0);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgb(0 0 0 / 10%);

  #payment-method {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    height: 100%;
  }

  #agreement {
    width: 100%;
    height: 200px;
    margin-top: 24px;
  }

  @media ${theme.device.medium} {
    overflow: visible;
    overflow-y: scroll;
    gap: 24px;

    width: 90%;
    height: 90%;
    padding: 56px 20px 30px;
  }
`

const CloseIcon = styled(Close)`
  cursor: pointer;

  position: absolute;
  z-index: 999;
  top: 12px;
  right: 12px;

  @media ${theme.device.medium} {
    top: 20px;
    right: 20px;
    width: 24px;
    height: 24px;
  }
`

const ButtonContainer = styled.div`
  width: 90%;
  padding: 24px;
`

const PaymentButton = styled.button`
  cursor: pointer;

  width: 100%;
  padding: 11px 22px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 17px;
  font-weight: 600;
  color: #4e5968;
  color: ${theme.colors.white};

  background-color: ${theme.colors.mint_3};
  border: none;
  border-radius: 8px;
`
